// Card texts in the user's app language. Pure functions, ported from the handoff package
// (tixu-ai-tools-handoff-locale/src/localizeCardSet.ts). Invariants:
//   · ids, order, icon, mode and model always come from the English set;
//   · a card's texts are taken all-or-nothing: if any text of a translated card is missing,
//     the whole card stays English, so one card never mixes two languages;
//   · inputs are never mutated; every call returns fresh objects.
import type { CardSet, ToolCard } from "./profession-cards";
import { resolveLanguageCode } from "i18n/resolve-language";

export type CardLanguage = "en" | "es" | "de" | "fr" | "it" | "ja" | "pt_br" | "ar";

/** Languages the data has card texts for (data/aiToolsProfession/locales + English). */
export const CARD_LANGUAGES: readonly CardLanguage[] = ["en", "es", "de", "fr", "it", "ja", "pt_br", "ar"];

/** Languages that take part in the test. A UI language outside the list sees today's /ai-tools. */
export const AI_TOOLS_CARD_LANGUAGES: readonly CardLanguage[] = ["en", "es", "de", "fr", "it", "ja", "pt_br", "ar"];

const RTL_CARD_LANGUAGES: readonly CardLanguage[] = ["ar"];

export type LocalizedCardSet = {
    /** The set to render; its `lead` is the first Quick start tab label in the card language. */
    set: CardSet;
    /** Language the card texts are really in (analytics `ui_language`); "en" when the set fell back. */
    language: CardLanguage;
    /** Direction of the card texts, set as `dir` on them: it is the served language's, not the interface's, when the set fell back to English. */
    dir: "ltr" | "rtl";
};

/** App i18n code → card language: "pt-BR" and "pt" → "pt_br", "ar-EG" → "ar" (see resolveLanguageCode). */
export const normalizeAppLanguage = (code: string | null | undefined): CardLanguage =>
    resolveLanguageCode(code, CARD_LANGUAGES) ?? "en";

export const isCardLanguageEnabled = (code: string | null | undefined): boolean =>
    AI_TOOLS_CARD_LANGUAGES.includes(normalizeAppLanguage(code));

const filled = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

const usable = (en: ToolCard, tr: ToolCard | undefined): tr is ToolCard => {
    if (!tr || tr.id !== en.id) return false;
    if (![tr.title, tr.guide, tr.placeholder, tr.sample].every(filled)) return false;
    if (!en.titleLines) return true;
    return Array.isArray(tr.titleLines) && tr.titleLines.length === 2 && tr.titleLines.every(filled);
};

/** An untranslated set ships as an English copy — it counts as English, not as a translation. */
const sameTexts = (a: ToolCard, b: ToolCard): boolean =>
    a.title === b.title &&
    a.guide === b.guide &&
    a.placeholder === b.placeholder &&
    a.sample === b.sample &&
    (a.titleLines ?? []).join("\n") === (b.titleLines ?? []).join("\n");

const buildCard = (en: ToolCard, texts: ToolCard): ToolCard => {
    const titleLines = en.titleLines ? [...(texts.titleLines ?? en.titleLines)] : undefined;
    return {
        id: en.id,
        title: texts.title,
        ...(titleLines ? { titleLines } : {}),
        icon: en.icon,
        guide: texts.guide,
        placeholder: texts.placeholder,
        sample: texts.sample,
        mode: en.mode,
        model: en.model
    };
};

/**
 * @param en          the English set chosen by resolveCardSet
 * @param translated  the same set in `language`; null when the language is English or the file did not load
 */
export const localizeCardSet = (en: CardSet, translated: CardSet | null | undefined, language: string): LocalizedCardSet => {
    const requested = normalizeAppLanguage(language);
    const tr = requested !== "en" && translated && translated.id === en.id ? translated : null;
    const byId = new Map<string, ToolCard>();
    if (tr) for (const c of [...(tr.popular ?? []), ...(tr.quickStart ?? [])]) if (c && typeof c.id === "string") byId.set(c.id, c);

    let translatedCards = 0;
    const localizeCard = (c: ToolCard): ToolCard => {
        const t = byId.get(c.id);
        if (usable(c, t) && !sameTexts(c, t)) {
            translatedCards++;
            return buildCard(c, t);
        }
        return buildCard(c, c);
    };
    const popular = en.popular.map(localizeCard);
    const quickStart = en.quickStart.map(localizeCard);

    // Nothing translated → the set is English, its tab label too: no mixing between the tab and its cards.
    const resolved: CardLanguage = translatedCards > 0 ? requested : "en";
    const lead = resolved !== "en" && tr && filled(tr.lead) ? tr.lead : en.lead;

    return {
        set: { id: en.id, kind: en.kind, name: en.name, lead, icon: en.icon, popular, quickStart },
        language: resolved,
        dir: RTL_CARD_LANGUAGES.includes(resolved) ? "rtl" : "ltr"
    };
};
